import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { installIdempotentRetry } from "../lib/httpRetry";
import BrandWordmarkText from "../components/BrandWordmarkText";
import { MarketingTopBar, SiteFooter } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import { ScrollProgressBar } from "../components/marketing/scrollMotion";
import MarketingSeo, { seoOrigin } from "../components/marketing/MarketingSeo";
import { useToast } from "../components/ui/useToast";
import { Button, Badge, EmptyState, ErrorState, Skeleton, SkeletonText } from "../components/ui/primitives";
import { Markdown } from "../components/ui/Markdown";
import { LifeBuoy, Search, ArrowLeft, ChevronRight, ArrowRight, Eye, Newspaper, LogIn } from "lucide-react";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";
import { SUPPORT_EMAIL } from "../lib/contactInfo";
import { useAuth } from "../context/AuthContext";
import {
  HELP_INDEX_EXCLUDED_CATEGORIES,
  groupHelpArticles,
  orderHelpCategories,
} from "./help/helpTaxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
// INFRA-8: `/help` and `/contact` are PRERENDERED marketing routes, so scripts/prerender.mjs's
// proxy retry protects the SNAPSHOT — nothing protected the reader's browser, and one ambient
// ALB 502 on these public GETs renders the empty rail / "Couldn't open this article" toast.
// Same shared rule as every other transport (src/lib/httpRetry.js); the POST below is never
// replayed because the rule is idempotent-methods-only.
const pub = installIdempotentRetry(axios.create({ baseURL: API }));

export default function Help() {
  const toast = useToast();
  const navigate = useNavigate();
  // /help is a public marketing route that lives inside AuthProvider, so it can tell a signed-in
  // reader that the in-app Help Center carries the operational how-tos this page deliberately
  // does not (`user` is null while the session check runs, false when signed out).
  const { user, can } = useAuth();
  const { articleSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(null);
  const [openingSlug, setOpeningSlug] = useState("");

  // The category filter is URL state (`/help?category=<name>`), so a chip is deep-linkable and a
  // /resources guide can hand a reader straight to the matching topic instead of the whole index.
  const cat = searchParams.get("category") || "";
  const setCat = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("category", next);
    else params.delete("category");
    params.delete("article"); // never carry the legacy alias into a filter change
    setSearchParams(params);
  }, [searchParams, setSearchParams]);
  // Remembered so "Back to all articles" returns to the filter the reader came from without
  // hanging a query string off every crawlable article URL.
  const lastCatRef = useRef("");
  useEffect(() => { if (!articleSlug) lastCatRef.current = cat; }, [articleSlug, cat]);

  const load = useCallback(() => {
    setItems(null);
    setError(false);
    pub.get("/help/public/articles", { params: { q, category: cat } })
      .then(({ data }) => setItems(data.items))
      .catch(() => { setItems([]); setError(true); });
  }, [q, cat]);
  useEffect(() => { pub.get("/help/public/categories").then(({ data }) => setCats(data.items)).catch(() => setCats([])); }, []);
  // Clear the results the moment the query or category changes, not 250ms later when the debounced
  // request finally fires: `isFiltered` flips instantly, so a stale list would render for a beat
  // under the new filter's heading — the whole 44-article set, FAQ bucket included, briefly
  // labelled "N articles in Getting Started". The debounce still belongs to the NETWORK call.
  useEffect(() => {
    setItems(null);
    setError(false);
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!e.currentTarget.checkValidity()) {
      document.querySelector('[data-testid="public-help-search"]')?.focus();
      e.currentTarget.reportValidity();
      return;
    }
    setActive(null);
    load();
  };

  // PGS-5/SEO-3 — articles live at PATH-BASED, crawlable URLs: /help/articles/<slug> (routed in
  // App.js to this same page). The legacy /help?article=<slug> deep link (older Aira "From the
  // Help Center" links, shared URLs) client-redirects onto the path so both keep resolving while
  // the canonical is always the path URL.
  //
  // Since the index cards are real anchors (a crawler has to see /help/articles/<slug> in the
  // markup), the ROUTE is the only trigger for a fetch: click, browser Back/Forward, a shared link
  // and a prerender all take the same path. `reqRef` is the staleness guard — two fast clicks used
  // to leave the second article stranded behind an `openingSlug` early return.
  const navigateSelf = useNavigate();
  const openedDeepRef = useRef("");
  const reqRef = useRef(0);

  const loadArticle = useCallback(async (slug) => {
    const seq = ++reqRef.current;
    setOpeningSlug(slug);
    try {
      const { data } = await pub.get(`/help/public/articles/${slug}`);
      if (reqRef.current !== seq) return;
      setActive(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      if (reqRef.current === seq) toast.error("Couldn't open this article. Please try again.");
    } finally {
      if (reqRef.current === seq) setOpeningSlug("");
    }
  }, [toast]);

  const articlePath = (slug) => `/help/articles/${encodeURIComponent(slug)}`;
  const indexPath = (category) => (category ? `/help?category=${encodeURIComponent(category)}` : "/help");

  const closeArticle = () => {
    reqRef.current += 1; // cancel any in-flight open
    setActive(null);
    setOpeningSlug("");
    openedDeepRef.current = "";
    if (articleSlug) navigateSelf(indexPath(lastCatRef.current));
  };

  // Legacy alias: /help?article=<slug> → /help/articles/<slug> (replace, so Back skips the alias).
  const legacySlug = searchParams.get("article") || "";
  useEffect(() => {
    if (legacySlug && !articleSlug) {
      navigateSelf(`/help/articles/${encodeURIComponent(legacySlug)}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacySlug, articleSlug]);

  // Route-driven open/close: the path param is the source of truth, so browser Back/Forward and
  // direct loads all resolve the same way an in-page click does.
  useEffect(() => {
    if (articleSlug && articleSlug !== openedDeepRef.current) {
      openedDeepRef.current = articleSlug;
      loadArticle(articleSlug);
    } else if (!articleSlug && (active || openingSlug)) {
      reqRef.current += 1;
      setActive(null);
      setOpeningSlug("");
      openedDeepRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleSlug]);

  const isFiltered = !!q || !!cat;
  // Chips read in the same documented journey order as the grouped index, not the API's
  // alphabetical order (GET /help/public/categories sorts by name).
  const orderedCats = orderHelpCategories(cats.map((c) => c.category));
  // The in-app Help Center is audience-scoped (routers/help.py `_article_visible`): it adds
  // operational how-tos this public page deliberately never publishes. Only say so to someone who
  // can actually open it.
  const showInAppNote = !!user && can("help:read");

  // An open article owns the head outright (title/description/canonical/Article schema) via the
  // conditional MarketingSeo below — the old document.title-only refinement left the index
  // canonical on every article view, which is why none of the 40+ public guides were indexable.
  const activeSeo = active && articleSlug === active.slug ? {
    title: `${active.title} | Vidyapeeth360 Help`.length <= 60
      ? `${active.title} | Vidyapeeth360 Help`
      : String(active.title),
    description: String(active.excerpt || active.summary || `${active.title} — a Vidyapeeth360 Help Center guide.`)
      .replace(/\s+/g, " ").trim().slice(0, 155),
    path: `/help/articles/${encodeURIComponent(active.slug)}`,
    schema: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: active.title,
      articleSection: active.category || undefined,
      url: `${seoOrigin}/help/articles/${encodeURIComponent(active.slug)}`,
      // Real timestamps only — the KB API exposes updated_at; no fabricated datePublished.
      ...(active.updated_at ? { dateModified: String(active.updated_at) } : {}),
      publisher: { "@type": "Organization", name: "Vidyapeeth360" },
    },
  } : null;

  return (
    <MarketingMotion>
    <div className="relative min-h-screen bg-surface landing-page">
      {activeSeo ? (
        <MarketingSeo
          title={activeSeo.title}
          description={activeSeo.description}
          path={activeSeo.path}
          type="article"
          schema={activeSeo.schema}
        />
      ) : !articleSlug && (
        <MarketingSeo
          title="Help Center — School ERP Guides & FAQs | Vidyapeeth360"
          description="Guides, how-tos and answers for Vidyapeeth360 — set up your school, collect fees online, message parents on WhatsApp and get the most from Aira AI."
          path="/help"
        />
      )}
      {/* Soft dual-glow background — static depth, without decorative ambient motion. */}
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-[680px] overflow-hidden" aria-hidden="true">
        <div className="absolute -top-28 -left-24 w-[540px] h-[540px] rounded-full bg-primary/10 blur-[130px]" />
        <div className="absolute top-24 -right-24 w-[480px] h-[480px] rounded-full bg-orange/10 blur-[130px]" />
      </div>

      {/* WP-07: /help uses the ONE marketing header — its bespoke glass-pill navbar (a third
          header variant with its own md: breakpoint and "Back to Website" affordance) is gone. */}
      <ScrollProgressBar />
      <MarketingTopBar />

      {/* Hero — light, premium, integrated with the site theme */}
      <section className="relative">
        <div className="max-w-3xl mx-auto px-5 pt-12 pb-10 sm:pt-16 sm:pb-14 text-center">
          {/* WP-08: above-the-fold content paints at opacity 1 — no entry fades on the hero. */}
          <span className="inline-flex items-center gap-2 rounded-full border border-orange-border/60 bg-orange-soft px-3.5 py-1.5 text-[13px] font-semibold text-orange-text">
            <LifeBuoy className="w-4 h-4" aria-hidden="true" /> Help Center
          </span>
          <h1 className="mt-4 font-heading font-extrabold text-[32px] sm:text-[46px] text-ink leading-[1.08] tracking-tight">
            How can we <span className="text-primary">help you?</span>
          </h1>
          <p className="mt-3.5 text-ink-2 text-[16px] sm:text-[17px] leading-relaxed max-w-xl mx-auto">
            Guides and answers for <BrandWordmarkText /> — setting up your school, importing records, collecting fees and meeting DPDP duties.
          </p>
          <form
            role="search"
            onSubmit={handleSearchSubmit}
            className="relative max-w-xl mx-auto mt-7"
          >
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
            <input
              type="search"
              value={q}
              onChange={(e) => { setQ(e.target.value); if (active) closeArticle(); }}
              aria-label="Search the Help Center"
              placeholder="Search help articles…"
              className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-surface border border-line text-ink shadow-card outline-none focus:border-accent focus:shadow-elevated focus:outline-none focus-visible:ring-2 ring-primary transition-[border-color,box-shadow]"
              data-testid="public-help-search"
            />
          </form>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-5 pb-24 lg:pb-16">
        {active ? (
          <div className="max-w-3xl mx-auto animate-fade-in" data-testid="public-article-view">
            <button type="button" onClick={closeArticle} className="inline-flex items-center gap-1.5 text-sm text-accent font-medium mb-5 hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary" data-testid="public-article-back"><ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to all articles</button>
            <Badge tone="blue" className="mb-3">{active.category}</Badge>
            <h2 className="font-heading font-bold text-[26px] sm:text-[32px] text-ink leading-tight tracking-tight">{active.title}</h2>
            <p className="text-[12.5px] text-ink-subtle mt-2 flex items-center gap-2.5">
              {/* A single-digit view counter reads as emptiness, not popularity — earn it first. */}
              {Number(active.views) >= 50 && <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" aria-hidden="true" /> {active.views} views</span>}
              {active.updated_at && <span>Updated {new Date(String(active.updated_at)).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>}
            </p>
            {active.summary && (
              <div className="mt-5 rounded-2xl border border-accent/20 bg-accent/[0.05] p-4" data-testid="public-article-summary">
                <p className="text-[11px] font-bold uppercase tracking-wide text-accent mb-1">In short</p>
                <Markdown content={active.summary} className="text-[15px] text-ink-2 leading-relaxed [&_p]:my-1 [&_ul]:my-1.5 [&_ul]:ml-4" />
              </div>
            )}
            <Markdown
              content={active.body}
              headingBase={2}
              className="mt-6 text-[16px] text-ink-2 [&_h3]:text-[21px] [&_h3]:mt-9 [&_h3]:mb-2 [&_h3]:tracking-tight [&_h4]:text-[17.5px] [&_h4]:mt-7 [&_h4]:mb-1.5 [&_h5]:text-[16px] [&_h5]:mt-5 [&_p]:my-3 [&_p]:leading-[1.75] [&_ul]:my-3.5 [&_ol]:my-3.5 [&_ul]:ml-5 [&_ol]:ml-5 [&_li]:my-1.5 [&_li]:leading-relaxed [&_table]:text-[14px] [&_blockquote]:my-4 [&_blockquote]:py-1 [&_hr]:my-6"
            />
            {active.tags?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-8">{active.tags.map((t) => <span key={t} className="text-[11px] px-2 py-1 rounded-md bg-muted text-ink-subtle">#{t}</span>)}</div>}
            {!isFiltered && (items || []).filter((a) => a.category === active.category && a.slug !== active.slug).length > 0 && (
              <div className="mt-10" data-testid="public-article-related">
                <h3 className="font-heading font-semibold text-ink text-lg mb-3">Related articles</h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {(items || []).filter((a) => a.category === active.category && a.slug !== active.slug).slice(0, 4).map((a) => (
                    <li key={a.slug} className="flex">
                      <Link to={articlePath(a.slug)} data-testid={`public-related-${a.slug}`}
                        className="flex w-full flex-col text-left rounded-2xl border border-line p-4 bg-surface hover:border-accent/40 hover:shadow-elevated transition-[border-color,box-shadow] group focus:outline-none focus-visible:ring-2 ring-primary">
                        <span className="font-heading font-semibold text-ink text-[15px] leading-snug group-hover:text-accent transition-colors">{a.title}</span>
                        <span className="text-[13px] text-ink-subtle mt-1 line-clamp-2">{a.excerpt}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-muted/60 px-5 py-4" data-testid="help-article-escalation">
              <p className="text-sm text-ink-2 font-medium">Didn't find what you needed?</p>
              <div className="flex gap-2">
                <Link to="/contact" className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface text-ink-2 text-[13px] font-semibold px-3.5 py-1.5 hover:border-accent/50 hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 ring-primary">Contact support</Link>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface text-ink-2 text-[13px] font-semibold px-3.5 py-1.5 hover:border-accent/50 hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 ring-primary">Email us</a>
              </div>
            </div>
            {/* Theme-stable scene navy (the shared conversion close band, --vp-close-band-*
                in index.css): `bg-royal` flips to near-white in the dark marketing palette, which
                would invert this card, so these tokens are fixed in both themes. White heading on
                the lightest stop is 13.8:1; the /65 subcopy is >=6.6:1. */}
            <div className="mt-6 rounded-2xl bg-gradient-to-br from-[var(--vp-close-band-from)] via-[var(--vp-close-band-via)] to-[var(--vp-close-band-to)] p-6 text-center relative overflow-hidden" data-testid="help-article-close-card">
              <div className="absolute inset-0 bg-grid opacity-20" />
              <div className="relative">
                <h3 className="font-heading font-bold text-white text-xl">Ready to try <BrandWordmarkText light />?</h3>
                <p className="text-white/65 text-sm mt-1.5">Start a 90-day free trial — no card.</p>
                {/* A11Y-1/PGS-1: white-on-navy CTA (the SegmentPage.js precedent) — the old
                    variant="sky" was white-on-#0EA5E9 ≈2.77:1, an AA failure the axe gate missed
                    because the .bg-grid overlay bucketed it "incomplete". bg-white gives axe a
                    determinate solid background; the LITERAL #1D4ED8 (not var-backed text-primary,
                    which lightens to #5B95FF ≈2.9:1-on-white in the dark marketing palette) holds
                    6.3:1 on white in BOTH themes. */}
                <Button className="mt-4 !bg-white !text-[#1D4ED8] hover:!bg-white/90 shadow-lg" onClick={() => navigate("/register")} data-testid="help-article-cta">{CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" /></Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {showInAppNote && (
              <p
                className="mb-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-2xl border border-line bg-muted/60 px-4 py-3 text-center text-[13.5px] leading-relaxed text-ink-2"
                data-testid="help-signed-in-note"
              >
                <LogIn className="w-4 h-4 text-accent" aria-hidden="true" />
                <span>You're signed in — the Help Center inside your workspace adds step-by-step how-tos for the modules and permissions your school has.</span>
                <Link to="/app/help" className="font-semibold text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary" data-testid="help-in-app-link">Open it in the app</Link>
              </p>
            )}
            {orderedCats.length > 0 && (
              <div className="flex gap-2 mb-8 overflow-x-auto pb-1 -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:pb-0">
                <button type="button" onClick={() => setCat("")} aria-pressed={!cat} className={`px-3.5 py-1.5 rounded-full text-sm border transition-[background-color,border-color,color] focus:outline-none focus-visible:ring-2 ring-primary ${!cat ? "bg-primary text-white border-accent" : "bg-surface border-line text-ink-2 hover:border-accent/40 hover:text-accent"}`} data-testid="public-cat-all">All topics</button>
                {orderedCats.map((c) => (
                  <button type="button" key={c} onClick={() => setCat(c)} aria-pressed={cat === c} className={`px-3.5 py-1.5 rounded-full text-sm border transition-[background-color,border-color,color] focus:outline-none focus-visible:ring-2 ring-primary ${cat === c ? "bg-primary text-white border-accent" : "bg-surface border-line text-ink-2 hover:border-accent/40 hover:text-accent"}`} data-testid={`public-cat-${c}`}>{c}</button>
                ))}
              </div>
            )}
            {items === null ? (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="public-help-loading" aria-busy="true">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="rounded-2xl border border-line p-5 bg-surface">
                    <Skeleton className="w-10 h-10 rounded-xl mb-3" />
                    <SkeletonText lines={3} />
                  </div>
                ))}
              </div>
            ) : error ? (
              <ErrorState title="Couldn't load articles" desc="We could not load the help articles just now. Please try again." onRetry={load} retryLabel="Try again" />
            ) : !items.length ? (
              <div data-testid="public-help-empty">
                <EmptyState
                  title={isFiltered ? "No matching articles" : "No articles yet"}
                  desc={isFiltered ? "No articles matched. Try another search — or ask us directly." : "No help articles are available yet."}
                  action={isFiltered ? (
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button variant="ghost" onClick={() => { setQ(""); setCat(""); }}>Clear filters</Button>
                      <Button variant="outline" onClick={() => navigate("/contact")} data-testid="help-empty-contact">Contact support</Button>
                    </div>
                  ) : undefined}
                />
              </div>
            ) : (
              (() => {
                // Index IA (per-page review P1): the unfiltered home groups guides by category
                // instead of one undifferentiated wall, and the legacy FAQ Q&As (near-duplicates
                // of the guides' "In short" answers) stay out of the default index — still fully
                // reachable via the FAQ chip, search, and Aira grounding.
                const visible = isFiltered
                  ? items
                  : items.filter((a) => !HELP_INDEX_EXCLUDED_CATEGORIES.includes(a.category));
                // A card is a REAL anchor: /help/articles/<slug> is prerendered and sitemapped, so
                // the index has to hand a crawler an href, not an onClick. The route effect above
                // does the fetching, which is why the busy affordance can stay purely visual.
                const card = (a) => (
                  <li key={a.id} className="flex">
                    <Link
                      to={articlePath(a.slug)}
                      aria-busy={openingSlug === a.slug ? true : undefined}
                      className={`flex w-full flex-col text-left rounded-2xl border border-line p-5 hover:shadow-elevated hover:border-accent/30 hover:-translate-y-0.5 transition-[transform,border-color,box-shadow,opacity] duration-200 bg-surface group focus:outline-none focus-visible:ring-2 ring-primary ${openingSlug === a.slug ? "opacity-70 cursor-wait" : ""}`}
                      data-testid={`public-article-${a.slug}`}
                    >
                      <span className="flex items-center justify-between mb-2 min-h-[20px]">
                        {/* In the filtered/search view the category label carries real information;
                            in grouped mode the section header already says it. */}
                        {isFiltered
                          ? <span className="text-[11px] font-bold uppercase tracking-wide text-accent">{a.category}</span>
                          : <span aria-hidden="true" />}
                        {openingSlug === a.slug ? <span className="text-xs text-ink-subtle">Opening…</span> : <ChevronRight className="w-4 h-4 text-ink-subtle group-hover:text-accent group-hover:translate-x-0.5 transition-[transform,color]" aria-hidden="true" />}
                      </span>
                      {/* A heading is not phrasing content, so it may not live inside the link
                          (it would flatten into the link's accessible name anyway). Styled spans;
                          the <ul>/<li> structure carries the navigation semantics. */}
                      <span className="font-heading font-semibold text-ink">{a.title}</span>
                      <span className="text-sm text-ink-subtle mt-1.5 line-clamp-2">{a.excerpt}</span>
                    </Link>
                  </li>
                );
                if (isFiltered) {
                  return (
                    <>
                      <p className="text-sm text-ink-2 mb-4" data-testid="public-help-count">
                        {visible.length} article{visible.length === 1 ? "" : "s"}{cat ? ` in ${cat}` : ""}{q ? ` matching “${q}”` : ""}
                      </p>
                      <ul className="grid sm:grid-cols-2 gap-4" aria-live="polite" aria-label={`${visible.length} article${visible.length === 1 ? "" : "s"}`}>
                        {visible.map(card)}
                      </ul>
                    </>
                  );
                }
                // Auto-arrangement: every category the API returns is placed by the ONE documented
                // journey order (pages/help/helpTaxonomy.js). A category that order does not know
                // still renders — after the known ones, alphabetically — so a newly authored
                // category can never silently disappear from the index.
                const groups = groupHelpArticles(visible);
                return (
                  <div className="space-y-10" aria-live="polite" aria-label={`${visible.length} articles in ${groups.length} topics`} data-testid="public-help-groups">
                    {groups.map(({ category: c, articles: arr }) => (
                      <section key={c} aria-labelledby={`help-cat-${c}`} data-testid={`public-help-group-${c}`}>
                        <div className="flex items-baseline gap-2.5 mb-4">
                          <h2 id={`help-cat-${c}`} className="font-heading font-bold text-ink text-[19px] tracking-tight">{c}</h2>
                          <span className="text-[13px] text-ink-subtle">{arr.length}</span>
                        </div>
                        <ul className="grid sm:grid-cols-2 gap-4">{arr.map(card)}</ul>
                      </section>
                    ))}
                  </div>
                );
              })()
            )}
            {/* Cross-discovery: /help and /resources are two governed libraries — the knowledge
                base is platform-admin-owned CMS content, the resource library is code-authored
                under an editorial process — and a reader who lands on one used to never learn the
                other existed. One honest line each way, no content moved. */}
            {!error && (
              <aside
                className="mt-12 rounded-2xl border border-line bg-surface p-5 sm:p-6"
                aria-labelledby="help-crosslink-heading"
                data-testid="help-resources-crosslink"
              >
                <p className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.12em] text-accent">
                  <Newspaper className="w-4 h-4" aria-hidden="true" /> The resource library
                </p>
                <h2 id="help-crosslink-heading" className="mt-2 font-heading font-bold text-ink text-[19px] tracking-tight">
                  Guides &amp; resources
                </h2>
                <p className="mt-1.5 max-w-2xl text-[14.5px] leading-relaxed text-ink-2">
                  These articles are how-to guides for using the product. Our resource library is the
                  other half: longer briefings on running an Indian school — reconciling fee collection,
                  meeting DPDP duties, migrating without unsettling parents, and evaluating school
                  software before you buy it.
                </p>
                <Link
                  to="/resources"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-ink-2 transition-colors hover:border-accent/50 hover:text-accent focus:outline-none focus-visible:ring-2 ring-primary"
                  data-testid="help-resources-link"
                >
                  Open the resource library <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </Link>
              </aside>
            )}
            {/* The index closes the way an article does: a human escalation path, then the ask. */}
            {items !== null && !error && items.length > 0 && (
              <>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-muted/60 px-5 py-4" data-testid="help-index-escalation">
                  <p className="text-sm text-ink-2 font-medium">Didn't find what you needed?</p>
                  <div className="flex gap-2">
                    <Link to="/contact" className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface text-ink-2 text-[13px] font-semibold px-3.5 py-1.5 hover:border-accent/50 hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 ring-primary">Contact support</Link>
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface text-ink-2 text-[13px] font-semibold px-3.5 py-1.5 hover:border-accent/50 hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 ring-primary">Email us</a>
                  </div>
                </div>
                {/* Same theme-stable navy close card + AA CTA recipe as the article view above
                    (A11Y-1/PGS-1 — see that comment for the ratios). */}
                <div className="mt-6 rounded-2xl bg-gradient-to-br from-[var(--vp-close-band-from)] via-[var(--vp-close-band-via)] to-[var(--vp-close-band-to)] p-6 text-center relative overflow-hidden" data-testid="help-index-close-card">
                  <div className="absolute inset-0 bg-grid opacity-20" />
                  <div className="relative">
                    <h3 className="font-heading font-bold text-white text-xl">Ready to try <BrandWordmarkText light />?</h3>
                    <p className="text-white/65 text-sm mt-1.5">Start a 90-day free trial — no card.</p>
                    <Button className="mt-4 !bg-white !text-[#1D4ED8] hover:!bg-white/90 shadow-lg" onClick={() => navigate("/register")} data-testid="help-index-cta">{CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" /></Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>

      <SiteFooter />
    </div>
    </MarketingMotion>
  );
}
